import type { ContainerLayoutViewModel, ContainerViewModel, WidgetTabViewModel } from '../types';
import type { IfThenResearchPlan, Idea } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type {
    TradingViewChartInterval,
    StrategyCreation,
    WidgetCreationInfo,
    WidgetType,
    EventType,
} from '@thinkalpha/ui-types/contracts/workspace';

export type AddContainerAction = {
    type: 'addContainer';
    container: ContainerViewModel;
    initialTab: WidgetTabViewModel;
    layout: ContainerLayoutViewModel;
};

export const addContainer = (
    container: ContainerViewModel,
    initialTab: WidgetTabViewModel,
    layout: ContainerLayoutViewModel,
): AddContainerAction => ({
    type: 'addContainer',
    container,
    initialTab,
    layout,
});

export type RemoveContainerAction = {
    type: 'removeContainer';
    containerId: string;
};

export const removeContainer = (containerId: string): RemoveContainerAction => ({
    type: 'removeContainer',
    containerId,
});

export type SetActiveContainerAction = {
    type: 'setActiveContainer';
    containerId: string | undefined;
};

export const setActiveContainer = (containerId: string | undefined): SetActiveContainerAction => ({
    type: 'setActiveContainer',
    containerId,
});

export type AddWidgetToDashboardAction = {
    type: 'addWidgetToDashboard';
    widget: WidgetCreationInfo;
    preferredDimensions?: { w: number; h: number };
};

export const addWidgetToDashboard = (widgetType: WidgetType): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: { creationType: 'from-widget-default', widgetType },
});

export const addWidgetToDashboardWithClickPosition = (
    widgetType: WidgetType,
    clickPosition: { x: number; y: number },
): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: { creationType: 'from-widget-default', widgetType, workspaceLocation: clickPosition },
});

export const addWidgetToDashboardInRect = (
    widgetType: WidgetType,
    targetRect: { x: number; y: number; w: number; h: number },
): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: { creationType: 'from-widget-default', widgetType, targetRect },
});

export const addWidgetToTemplateSlot = (
    widgetType: WidgetType,
    targetRect: { x: number; y: number; w: number; h: number },
    slotId: string,
): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'from-widget-default',
        widgetType,
        targetRect,
        fillTargetRect: true,
        templateSlotId: slotId,
    },
});

export const createAlphaEyeFromSymbol = ({
    defaultSymbol,
    channelId,
}: {
    defaultSymbol: string;
    channelId: string | null;
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-alpha-eye',
        defaultSymbol,
        channelId,
    },
});
export const createChartFromSymbol = ({
    defaultSymbol,
    interval,
    channelId,
}: {
    defaultSymbol: string;
    interval?: TradingViewChartInterval;
    channelId: string | null;
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-tradingview-chart',
        defaultSymbol,
        interval,
        channelId,
    },
});
export const createNewsFromSymbol = ({
    defaultSymbol,
    channelId,
}: {
    defaultSymbol: string;
    channelId: string | null;
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-news',
        defaultSymbol,
        channelId,
    },
});

export const createSearchAlphaWithIdea = ({
    channelId,
    idea,
}: {
    channelId: string | null;
    idea: Idea & { plan: IfThenResearchPlan };
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-searchalpha',
        channelId,
        idea,
    },
});

export const createNewsWithSymbolFilter = ({
    channelId,
    symbolFilter,
}: {
    channelId: string | null;
    symbolFilter: string | null;
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-news',
        channelId,
        symbolFilter,
    },
});

export const createEventsWithSymbolFilter = ({
    channelId,
    symbolFilter,
    eventTypes,
}: {
    channelId: string | null;
    symbolFilter: string | null;
    eventTypes?: EventType[];
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-events',
        channelId,
        symbolFilter,
        eventTypes,
    },
});

/**
 * @deprecated We don't support creating results with an Idea literal any more, only Idea by ID
 */
export const createResultsWithStrategy = ({
    defaultSymbol,
    channelId,
    strategies,
    preferredDimensions,
    columnTemplate,
    universeId,
    filter,
    scrolling,
    columnOptions,
}: {
    defaultSymbol: string;
    channelId: string | null;
    strategies: (Idea | string)[];
    preferredDimensions?: { w: number; h: number };
    columnTemplate?: StrategyCreation['columnTemplate'];
    universeId?: StrategyCreation['universeId'];
    filter?: StrategyCreation['filter'];
    scrolling?: StrategyCreation['scrolling'];
    columnOptions?: StrategyCreation['columnOptions'];
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'new-strategy',
        defaultSymbol,
        channelId,
        strategies,
        columnTemplate,
        universeId,
        filter,
        scrolling,
        columnOptions,
    },
    preferredDimensions,
});

export const cloneWidgetToDashboard = ({
    containerId,
    tabId,
}: {
    containerId: string;
    tabId: string;
}): AddWidgetToDashboardAction => ({
    type: 'addWidgetToDashboard',
    widget: {
        creationType: 'cloned',
        baseWidgetTabId: tabId,
        baseWidgetContainerId: containerId,
    },
});
export type CycleActiveContainerAction = {
    type: 'cycleActiveContainer';
    containerId: string;
};

export const cycleActiveContainer = (containerId: string): CycleActiveContainerAction => ({
    type: 'cycleActiveContainer',
    containerId,
});

export type SetMaximizedContainerAction = {
    type: 'setMaximizedContainer';
    containerId: string | undefined;
};

export const setMaximizedContainer = (containerId: string | undefined): SetMaximizedContainerAction => ({
    type: 'setMaximizedContainer',
    containerId,
});

export type ContainerAction =
    | AddWidgetToDashboardAction
    | SetActiveContainerAction
    | AddContainerAction
    | RemoveContainerAction
    | CycleActiveContainerAction
    | SetMaximizedContainerAction;
